import React, { useState, useEffect, useCallback } from 'react';
import StrategyBuilder from '../Builder/StrategyBuilder'; // Import StrategyBuilder
import { ValidationMessage } from '../../common';
import { useTrendlineStrategy } from '../../../hooks/useTrendlineStrategy';
import { createEmptyStrategy } from '../../../utils/strategyUtils';
import { TrendLineDrawingProvider, useTrendLineDrawing } from '../../../contexts/TrendLineDrawingContext';

/**
 * CreateTrendlineStrategy - New strategy creation component using StrategyBuilder
 * 
 * @param {Object} props
 * @param {boolean} props.open - Whether dialog is open (for modal mode)
 * @param {Function} props.onClose - Close handler
 * @param {Function} props.onSuccess - Success callback with created strategy
 * @param {boolean} props.inline - Whether to render inline (not as modal)
 * @param {Object} props.initialData - Initial strategy data for editing
 * @param {Array} props.availableTrendLines - Trend lines from chart
 */
const CreateTrendlineStrategyInner = ({
  open,
  onClose,
  onSuccess,
  inline = false,
  initialData = null,
  availableTrendLines = [],
}) => {
  const {
    createStrategy,
    updateStrategy,
    validateStrategy,
    validationResult,
    loading,
    error,
    clearError,
    clearValidation,
    fetchRulesSchema,
    fetchIndicatorSchemas,
    rulesSchema,
    indicatorSchemas,
  } = useTrendlineStrategy();

  const [formData, setFormData] = useState(initialData || createEmptyStrategy());
  const [localErrors, setLocalErrors] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [schemasLoaded, setSchemasLoaded] = useState(false);

  // Get trendline drawing context - StrategyBuilder might access this via Context or passed props
  // StrategyBuilder takes availableTrendLines as prop.
  const { drawnTrendLines, clearAllTrendLines, getTrendLinesBySymbol } = useTrendLineDrawing();

  // Fetch schemas on mount
  useEffect(() => {
    const loadSchemas = async () => {
      await Promise.all([
        fetchRulesSchema(),
        fetchIndicatorSchemas(),
      ]);
      setSchemasLoaded(true);
    };
    loadSchemas();
  }, [fetchRulesSchema, fetchIndicatorSchemas]);

  // Reset form when dialog opens/closes or initialData changes
  useEffect(() => {
    if (open || inline) {
      setFormData(initialData || createEmptyStrategy());
      setLocalErrors({});
      clearValidation();
      clearError();
    }
  }, [open, inline, initialData, clearValidation, clearError]);

  // Clear trendline context when component unmounts or dialog closes
  useEffect(() => {
    return () => {
      // Clear drawn trendlines when leaving strategy creation
      clearAllTrendLines();
    };
  }, [clearAllTrendLines]);

  // Validate all sections
  const validateForm = async () => {
    clearValidation();
    const errors = {};

    // Basic Info validation
    if (!formData.name || formData.name.trim().length < 3) {
      errors.name = 'Strategy name must be at least 3 characters';
    }
    if (!formData.tickerName || formData.tickerName.trim().length === 0) {
      errors.tickerName = 'Ticker symbol is required';
    }

    // Risk Management validation
    if (formData.capitalAmount !== null && formData.capitalAmount !== undefined) {
      if (formData.capitalAmount < 1000) {
        errors.capitalAmount = 'Capital Amount must be at least ₹1,000';
      }
      if (formData.capitalAmount > 10000000) {
        errors.capitalAmount = 'Capital Amount cannot exceed ₹1,00,00,000';
      }
    }
    if (!formData.tradingType) {
      errors.tradingType = 'Trading Type is required';
    }
    if (!formData.orderType) {
      errors.orderType = 'Order Type is required';
    }
    // Lot Size: null is valid (defaults to 1); if provided must be >= 1
    if (formData.lotSize !== null && formData.lotSize !== undefined && formData.lotSize < 1) {
      errors.lotSize = 'Lot Size must be at least 1';
    }

    // Rule Groups validation
    // StrategyBuilder manages Sets which are RuleGroups pairs
    // Validation still checks ruleGroups array
    if (!formData.ruleGroups || formData.ruleGroups.length === 0) {
      errors.ruleGroups = 'At least one strategy set (Entry/Exit) is required';
    } else {
      // Ensure pairs are complete if strict pairing is enforced?
      // For now just check individual groups are not empty if desired.
      formData.ruleGroups.forEach((group, index) => {
        // StrategyBuilder allows adding empty sets, usually we want at least one condition?
        // Let's enforce reasonable defaults or warn.
        // errors[`group_${index}`] can be used if StrategyBuilder supported group level error display
      });
    }

    setLocalErrors(errors);

    if (Object.keys(errors).length > 0) {
      return false;
    }

    // API validation
    const result = await validateStrategy(formData);
    return result?.valid === true;
  };

  // Handle form submission
  const handleSubmit = async () => {
    const isValid = await validateForm();
    if (!isValid) return;

    try {
      let result;
      if (initialData?.id) {
        // Update existing strategy
        result = await updateStrategy(initialData.id, formData);
      } else {
        // Create new strategy
        result = await createStrategy(formData);
      }

      if (result) {
        setSnackbar({
          open: true,
          message: `Strategy ${initialData?.id ? 'updated' : 'created'} successfully!`,
          severity: 'success',
        });

        if (onSuccess) {
          onSuccess(result);
        }

        // Clear trendline context on successful save
        clearAllTrendLines();

        if (onClose) {
          onClose();
        }
      }
    } catch (err) {
      setSnackbar({
        open: true,
        message: error || 'Failed to save strategy',
        severity: 'error',
      });
    }
  };

  // Snackbar close handler
  const handleSnackbarClose = useCallback(() => {
    setSnackbar(prev => ({ ...prev, open: false }));
  }, []);

  // Builder Content
  const builderContent = (
    <StrategyBuilder
      formData={formData}
      onChange={setFormData}
      onSave={handleSubmit}
      onCancel={onClose} // pass onClose as Cancel handler
      loading={loading}
      errors={localErrors}
      availableTrendLines={[
        ...availableTrendLines,
        ...getTrendLinesBySymbol(formData.tickerName || 'TITAN'),
      ]}
      rulesSchema={rulesSchema}
      indicatorSchemas={indicatorSchemas}
    />
  );

  // Toast notification
  const toastElement = snackbar.open && (
    <div className="toast toast-bottom toast-center z-50">
      <div className={`alert ${snackbar.severity === 'error' ? 'alert-error' : 'alert-success'}`}>
        <span>{snackbar.message}</span>
        <button className="btn btn-ghost btn-xs" onClick={handleSnackbarClose}>✕</button>
      </div>
    </div>
  );

  // Inline mode
  if (inline) {
    return (
      <div className="h-full flex flex-col">
        {/* Validation Message if global error exists (e.g. from API) */}
        {/* StrategyBuilder displays localErrors, validationResult is API error */}
        {validationResult && (
          <div className="p-4 pb-0">
            <ValidationMessage validation={validationResult} showSuccess />
          </div>
        )}
        {/* We assume StrategyBuilder takes full height */}
        <div className="flex-1 overflow-hidden">
          {builderContent}
        </div>
        {toastElement}
      </div>
    );
  }

  // Modal mode
  if (!open) return null;

  return (
    <>
      <dialog className="modal modal-open">
        <div className="modal-box w-11/12 max-w-7xl h-[90vh] p-0 flex flex-col overflow-hidden">
          {/* Modal close handled by StrategyBuilder Cancel or X inside if added */}
          {/* StrategyBuilder has its own header with Cancel. 
                But for Modal we might want a clean wrapper.
                StrategyBuilder header works for both.
             */}
          {validationResult && (
            <div className="p-4 pb-0">
              <ValidationMessage validation={validationResult} showSuccess />
            </div>
          )}
          <div className="flex-1 overflow-hidden">
            {builderContent}
          </div>
        </div>
        <form method="dialog" className="modal-backdrop">
          <button onClick={onClose}>close</button>
        </form>
      </dialog>
      {toastElement}
    </>
  );
};

/**
 * CreateTrendlineStrategy - Wrapper component with TrendLineDrawingProvider
 */
const CreateTrendlineStrategy = (props) => {
  return (
    <TrendLineDrawingProvider>
      <CreateTrendlineStrategyInner {...props} />
    </TrendLineDrawingProvider>
  );
};

export default CreateTrendlineStrategy;
